import { useEffect, useMemo, useRef, useState } from "react";
import { type ChangeEvent } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { ProgressBar } from "primereact/progressbar";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { humanEndDate } from "sj-ui-toolkit/src/utils/dates";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { Skeleton } from "primereact/skeleton";
import {
  loadPersonalizedLessonAssignmentReportRows,
  type PersonalizedLessonAssignmentReportRow,
} from "@/api/personalizedLessonAssignments";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";

const EMPTY_ROW_TEXT = "No personalized lesson assignments found";

const dueDateTemplate = (rowData: PersonalizedLessonAssignmentReportRow | null) =>
  rowData == null ? (
    <Skeleton width="7rem" />
  ) : rowData.dueDate ? (
    humanEndDate(rowData.dueDate)
  ) : (
    "-"
  );

const statusTemplate = (rowData: PersonalizedLessonAssignmentReportRow | null) =>
  rowData == null ? <Skeleton width="7rem" /> : <span>{rowData.statusName || "-"}</span>;

const textTemplate = (
  rowData: PersonalizedLessonAssignmentReportRow | null,
  field: keyof PersonalizedLessonAssignmentReportRow
) => {
  if (rowData == null) return <Skeleton width="8rem" />;
  return String(rowData[field] || "-");
};

const numberTemplate = (
  rowData: PersonalizedLessonAssignmentReportRow | null,
  field: "totalLessons" | "completedCount" | "notStartedCount"
) => (rowData == null ? <Skeleton width="4rem" /> : rowData[field]);

const progressTemplate = (rowData: PersonalizedLessonAssignmentReportRow | null) => {
  if (rowData == null) return <Skeleton width="8rem" />;

  const progress = rowData.progress;
  if (!progress.required) return "--";

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <ProgressBar
          value={rowData.progressPercentage}
          showValue={false}
          style={{ height: "0.5rem" }}
        />
      </div>
      <span className="text-sm">{rowData.progressPercentage}%</span>
    </div>
  );
};

export default function PersonalizedLessonAssignmentReport() {
  useBreadcrumb({
    label: "Personalized Lesson Assignment",
    display: "page",
    route:
      AdminAppRoutes.routes.reporting.routes.assignments.routes.personalizedLessonAssignments.route,
  });

  const { showToast } = useToast();
  const { features, currentTenantName } = useAspenFeatures();
  const stateKey = `personalizedLessonAssignmentReportList-${getCurrentSelectedTenant() ?? ""}`;
  const dt = useRef<DataTable<DataTableValueArray>>(null);
  const [rows, setRows] = useState<PersonalizedLessonAssignmentReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    if (!currentTenantName || !features.adapt) {
      setIsLoading(false);
      return;
    }

    // `cancelled` is flipped by the cleanup when the tenant changes (or the
    // component unmounts), so a stale response can't overwrite newer data.
    let cancelled = false;
    setIsLoading(true);

    loadPersonalizedLessonAssignmentReportRows(currentTenantName)
      .then(data => {
        if (!cancelled) setRows(data);
      })
      .catch(error => {
        if (cancelled) return;
        console.error("Failed to load personalized lesson assignments", error);
        showToast({
          severity: "error",
          detail: "Failed to load personalized lesson assignments",
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentTenantName, features.adapt]);

  const storedRows = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(stateKey);
      if (!raw) return DEFAULT_ROW_COUNT;
      const parsed = JSON.parse(raw) as { rows?: number };
      return Number.isFinite(parsed?.rows) && (parsed.rows ?? 0) > 0
        ? parsed.rows!
        : DEFAULT_ROW_COUNT;
    } catch {
      return DEFAULT_ROW_COUNT;
    }
  }, []);

  const skeletonItems = useMemo(() => Array.from({ length: storedRows }, () => null), [storedRows]);

  const onGlobalFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const nextFilters = { ...filters };
    nextFilters.global.value = value;
    setFilters(nextFilters);
    setGlobalFilterValue(value);
  };

  const exportCsv = () => {
    const csvRows = rows.map(row => ({
      Email: row.email,
      "First name": row.firstName,
      "Last name": row.lastName,
      "Assignment name": row.assignmentName,
      "Due date": row.dueDate ? humanEndDate(row.dueDate) : "-",
      Status: row.statusName || "-",
      "Total lessons": row.totalLessons,
      Completed: row.completedCount,
      "Not started": row.notStartedCount,
      Progress: row.progressDisplay,
    }));
    const headers = Object.keys(csvRows[0] || {});
    const csv = [
      headers.join(","),
      ...csvRows.map(row =>
        headers
          .map(header => JSON.stringify((row as Record<string, string | number>)[header] ?? ""))
          .join(",")
      ),
    ].join("\n");

    downloadCsv(csv, "personalized_lesson_assignment_report.csv");
  };

  return (
    <>
      <div className="mb-4">
        <h1>Personalized Lesson Assignment</h1>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <IconField iconPosition="left" className="min-w-96">
          <InputIcon className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search by assignment, email, or name..."
            aria-label="Search personalized lesson assignments"
            className="w-full"
          />
        </IconField>
        <Button
          label="Export data"
          icon="pi pi-download"
          onClick={exportCsv}
          disabled={isLoading}
        />
      </div>

      <DataTable
        ref={dt}
        aria-label="Personalized lesson assignment reports"
        value={(isLoading ? skeletonItems : rows) as unknown as DataTableValueArray}
        filters={isLoading ? {} : filters}
        onFilter={event => setFilters(event.filters as typeof filters)}
        globalFilterFields={isLoading ? [] : ["email", "firstName", "lastName", "assignmentName"]}
        stateKey={stateKey}
        stateStorage="local"
        paginator
        rows={storedRows}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        emptyMessage={EMPTY_ROW_TEXT}
        removableSort
        tableClassName="dt-responsive-text"
      >
        <Column
          field="email"
          header="Email"
          sortable
          body={rowData => textTemplate(rowData, "email")}
        />
        <Column
          field="firstName"
          header="First name"
          sortable
          body={rowData => textTemplate(rowData, "firstName")}
        />
        <Column
          field="lastName"
          header="Last name"
          sortable
          body={rowData => textTemplate(rowData, "lastName")}
        />
        <Column
          field="assignmentName"
          header="Assignment name"
          sortable
          body={rowData => textTemplate(rowData, "assignmentName")}
        />
        <Column field="dueDate" header="Due date" sortable body={dueDateTemplate} />
        <Column
          field="statusCode"
          header="Status"
          sortable
          exportField="statusName"
          body={statusTemplate}
        />
        <Column
          field="totalLessons"
          header="Total lessons"
          sortable
          body={rowData => numberTemplate(rowData, "totalLessons")}
        />
        <Column
          field="completedCount"
          header="Completed"
          sortable
          body={rowData => numberTemplate(rowData, "completedCount")}
        />
        <Column
          field="notStartedCount"
          header="Not started"
          sortable
          body={rowData => numberTemplate(rowData, "notStartedCount")}
        />
        <Column
          field="progress"
          header="Progress"
          sortable
          sortField="progressPercentage"
          exportField="progressDisplay"
          body={progressTemplate}
        />
      </DataTable>
    </>
  );
}
